'use client';

import { apiClient } from '@/sdk/shared/apiClient';
import {
  User,
  UserRole,
  UserStatus,
  UsersListResponse,
  UserFilters,
  DashboardStats,
  RegisterUserRequest,
  RegisterUserResponse,
} from './types';

interface BackendUser {
  id: string;
  email: string;
  full_name?: string;
  fullName?: string;
  employee_id?: string;
  employeeId?: string;
  phone?: string;
  mobile?: string;
  role: string;
  status: string;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
}

interface BackendUsersResponse {
  success: boolean;
  data: BackendUser[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  filters: Record<string, unknown>;
  sort: Record<string, unknown>;
}

interface BackendUserSingleResponse {
  success: boolean;
  data: BackendUser;
}

interface BackendStatsResponse {
  success: boolean;
  data: DashboardStats;
}

// Get all users with optional filters
export async function getUsers(
  filters: UserFilters = {}
): Promise<UsersListResponse> {
  const params = new URLSearchParams();

  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.role) params.append('role', filters.role);
  if (filters.status) params.append('status', filters.status);
  if (filters.search) params.append('search', filters.search);
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

  const queryString = params.toString();
  const endpoint = queryString ? `/users?${queryString}` : '/users';

  const response = await apiClient.get<BackendUsersResponse>(endpoint);

  const users: User[] = response.data.map((raw) => ({
    id: raw.id,
    fullName: raw.full_name || raw.fullName || '',
    email: raw.email,
    employee_id: raw.employee_id || raw.employeeId,
    employeeId: raw.employee_id || raw.employeeId,
    phone: raw.phone || raw.mobile || undefined,
    role: (raw.role as UserRole) || UserRole.SALES,
    status: (raw.status as UserStatus) || UserStatus.ACTIVE,
    createdAt: raw.created_at || raw.createdAt || '',
    updatedAt: raw.updated_at || raw.updatedAt || '',
  }));

  return {
    users,
    total: response.pagination?.total || 0,
    page: response.pagination?.page || 1,
    limit: response.pagination?.limit || 10,
  };
}

// Get user by ID
export async function getUserById(userId: string): Promise<User> {
  const response = await apiClient.get<BackendUserSingleResponse>(`/users/${userId}`);
  const raw = response.data;

  return {
    id: raw.id,
    fullName: raw.full_name || raw.fullName || '',
    email: raw.email,
    employee_id: raw.employee_id || raw.employeeId,
    employeeId: raw.employee_id || raw.employeeId,
    phone: raw.phone || raw.mobile || undefined,
    role: (raw.role as UserRole) || UserRole.SALES,
    status: (raw.status as UserStatus) || UserStatus.ACTIVE,
    createdAt: raw.created_at || raw.createdAt || '',
    updatedAt: raw.updated_at || raw.updatedAt || '',
  };
}

// Get dashboard stats
export async function getDashboardStats(): Promise<DashboardStats> {
  const response = await apiClient.get<BackendStatsResponse>('/users/stats');
  return response.data;
}

// Register a new user to an existing tenant
export async function registerUser(userData: RegisterUserRequest): Promise<RegisterUserResponse> {
  const response = await apiClient.post<RegisterUserResponse>('/users/register', userData);
  return response;
}

export async function updateUser(userId: string, userData: { email?: string; fullName?: string; roleId?: string; status?: string; isDeleted?: boolean }): Promise<any> {
  const response = await apiClient.put<any>(`/admin/subadmins/${userId}`, userData);
  return response;
}

export async function resetUserPassword(userId: string, newPassword: string): Promise<any> {
  const response = await apiClient.post<any>(`/admin/subadmins/${userId}/reset-password`, { newPassword });
  return response;
}

export async function deleteUser(userId: string): Promise<any> {
  const response = await apiClient.delete<any>(`/admin/subadmins/${userId}`);
  return response;
}

// Admin Users API object
export const adminUsersApi = {
  getUsers,
  getUserById,
  getDashboardStats,
  registerUser,
  updateUser,
  resetUserPassword,
  deleteUser,
};

export default adminUsersApi;
